import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useStudio } from "../../studios/StudioContext";
import "./myBookings.css";

const CLASSES = [
  { id: "cls_mon", label: "Monday 10:00 AM — Iyengar Yoga (In-person)" },
  { id: "cls_wed", label: "Wednesday 6:00 PM — Iyengar Yoga (In-person)" },
  { id: "cls_fri", label: "Friday 10:00 AM — Iyengar Yoga (In-person)" },
  { id: "cls_sat", label: "Saturday 8:15 AM — Iyengar Yoga (Online)" },
  { id: "cls_sun", label: "Sunday 10:00 AM — Iyengar Yoga (Online)" },
];

const PASS_LABELS = {
  pass4:      "4-Class Pass",
  pass8:      "8-Class Pass",
  member2x:   "Membership (2×/week)",
  memberUnl:  "Monthly",
  private1:   "Private Pass (1 class)",
  private3:   "Private Pass (3 classes)",
  private10:  "Private Pass (10 classes)",
};

const CLASS_LABELS = Object.fromEntries(CLASSES.map(c => [c.id, c.label]));

function toLocalDateStr(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

// ── Reschedule Modal ──────────────────────────────────────
function RescheduleModal({ booking, email, onDone, onClose }) {
  const { apiBaseUrl: API } = useStudio();
  const [newClassId, setNewClassId] = useState(booking.classId);
  const [newDate, setNewDate]       = useState(booking.date);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const handleReschedule = async () => {
    if (!newClassId || !newDate) { setError("Please select a class and date."); return; }
    if (newClassId === booking.classId && newDate === booking.date) {
      setError("Please choose a different class or date."); return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/bookings/${booking._id}/reschedule-student`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, newClassId, newDate }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Could not reschedule."); return; }
      onDone("rescheduled");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Min date = tomorrow (can't reschedule to today or past)
  const tomorrow = toLocalDateStr(new Date(Date.now() + 86400000));

  return (
    <div className="mb-modal-overlay" onClick={onClose}>
      <div className="mb-modal" onClick={e => e.stopPropagation()}>
        <button className="mb-modal-close" onClick={onClose}>✕</button>
        <h2 className="mb-modal-title">Reschedule Booking</h2>
        <p className="mb-modal-sub">
          Currently: <strong>{CLASS_LABELS[booking.classId] ?? booking.classId}</strong>
          <br />
          <span className="mb-modal-date">{formatDate(booking.date)}</span>
        </p>

        <label className="mb-field-label">New class</label>
        <select
          className="mb-input"
          value={newClassId}
          onChange={e => setNewClassId(e.target.value)}
        >
          {CLASSES.map(c => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>

        <label className="mb-field-label">New date</label>
        <input
          type="date"
          className="mb-input"
          value={newDate}
          min={tomorrow}
          onChange={e => setNewDate(e.target.value)}
        />

        {error && <p className="mb-error">{error}</p>}

        <div className="mb-modal-actions">
          <button className="mb-btn-ghost" onClick={onClose}>Go back</button>
          <button className="mb-btn-primary" onClick={handleReschedule} disabled={loading}>
            {loading ? "Rescheduling…" : "Confirm reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cancel Confirm Modal ───────────────────────────────────
function CancelModal({ booking, email, onDone, onClose }) {
  const { apiBaseUrl: API } = useStudio();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleCancel = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/bookings/${booking._id}/cancel-student`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Could not cancel."); return; }
      onDone("cancelled");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-modal-overlay" onClick={onClose}>
      <div className="mb-modal" onClick={e => e.stopPropagation()}>
        <button className="mb-modal-close" onClick={onClose}>✕</button>
        <h2 className="mb-modal-title">Cancel Booking</h2>
        <p className="mb-modal-sub">
          Are you sure you want to cancel your spot in:
        </p>
        <div className="mb-cancel-detail">
          {booking.classId.includes("__")
            ? booking.workshopTitle ?? "Workshop"
            : CLASS_LABELS[booking.classId] ?? booking.classId}
          <span>{formatDate(booking.date)}</span>
        </div>
        <p className="mb-cancel-note">
          {booking.passId
            ? "Your class credit will be restored to your pass."
            : "Drop-in payments are non-refundable."}
        </p>

        {error && <p className="mb-error">{error}</p>}

        <div className="mb-modal-actions">
          <button className="mb-btn-ghost" onClick={onClose}>Keep booking</button>
          <button className="mb-btn-danger" onClick={handleCancel} disabled={loading}>
            {loading ? "Cancelling…" : "Yes, cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Success Banner ─────────────────────────────────────────
function SuccessBanner({ type, onDismiss }) {
  const messages = {
    rescheduled: "Your booking has been rescheduled. A confirmation email is on its way.",
    cancelled:   "Your booking has been cancelled. A confirmation email is on its way.",
  };
  return (
    <div className="mb-success-banner">
      <span className="mb-success-icon">✓</span>
      <p>{messages[type]}</p>
      <button className="mb-success-dismiss" onClick={onDismiss}>✕</button>
    </div>
  );
}

// ── Booking Card ───────────────────────────────────────────
function BookingCard({ booking, isHighlighted, onReschedule, onCancel }) {
  const isPast = new Date(booking.date + "T23:59:00") < new Date();
  const cardRef = useRef(null);

  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isHighlighted]);

  return (
    <div
      ref={cardRef}
      className={`mb-booking-card ${isPast ? "mb-card-past" : ""} ${isHighlighted ? "mb-card-highlighted" : ""}`}
    >
      <div className="mb-card-left">
        <span className="mb-card-day">
          {new Date(booking.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" })}
        </span>
        <span className="mb-card-date-num">
          {new Date(booking.date + "T12:00:00").getDate()}
        </span>
        <span className="mb-card-month">
          {new Date(booking.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}
        </span>
      </div>
      <div className="mb-card-body">
        <p className="mb-card-class">
            {booking.classId.includes("__")
              ? booking.workshopTitle ?? "Workshop"
              : CLASS_LABELS[booking.classId] ?? booking.classId}
          </p>
          {booking.classId.includes("__") && (booking.sessionLabel || booking.sessionTime) && (
            <p className="mb-card-session">
              {booking.sessionLabel}{booking.sessionLabel && booking.sessionTime ? " · " : ""}{booking.sessionTime}
              </p>
          )}
        <p className="mb-card-meta">
          {booking.paymentType === "dropin" ? "Drop-in" : "Pass / Membership"}
          {" · "}
          <span className={`mb-status mb-status-${booking.status}`}>{booking.status}</span>
        </p>
      </div>
      {!isPast && booking.status === "confirmed" && (
        <div className="mb-card-actions">
          <button className="mb-card-btn mb-card-btn-reschedule" onClick={() => onReschedule(booking)}>
            Reschedule
          </button>
          <button className="mb-card-btn mb-card-btn-cancel" onClick={() => onCancel(booking)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────
export default function MyBookings() {
  const { apiBaseUrl: API } = useStudio();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail]               = useState("");
  const [submitted, setSubmitted]       = useState(false);
  const [bookings, setBookings]         = useState([]);
  const [activePass, setActivePass]     = useState(null); 
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [modal, setModal]               = useState(null); // { type: "reschedule"|"cancel", booking }
  const [success, setSuccess]           = useState(null); // "rescheduled" | "cancelled"

  const preloadId    = searchParams.get("cancel") || searchParams.get("reschedule");
  const preloadType  = searchParams.get("cancel") ? "cancel" : searchParams.get("reschedule") ? "reschedule" : null;

  const fetchBookings = async (emailVal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/students/${encodeURIComponent(emailVal)}`);
      const data = await res.json();
      if (!res.ok) { setError("Could not load bookings."); return; }

      // Only show upcoming + today, sorted soonest first
      const now = new Date();
      const upcoming = (data.bookings ?? [])
        .filter(b => new Date(b.date + "T23:59:00") >= now)
        .sort((a, b) => a.date.localeCompare(b.date));

      const past = (data.bookings ?? [])
        .filter(b => new Date(b.date + "T23:59:00") < now)
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 5); // last 5 only

      setBookings([...upcoming, ...past]);

      const ap = (data.passes ?? []).find(        // ← add from here
        p => p.active && p.type !== "dropin" && p.classesTotal > 0
      );
      setActivePass(ap ?? null);        

      // If a preload ID came in via URL, open the modal automatically
      if (preloadId && preloadType) {
        const target = data.bookings?.find(b => b._id === preloadId);
        if (target) {
          setModal({ type: preloadType, booking: target });
          // Clear the query params so a refresh doesn't re-open the modal
          setSearchParams({});
        }
      }
    } catch {
      setError("Could not load bookings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { setError("Please enter a valid email address."); return; }
    setSubmitted(true);
    fetchBookings(trimmed);
  };

  const handleDone = (type) => {
    setModal(null);
    setSuccess(type);
    fetchBookings(email.trim().toLowerCase());
  };

  const upcoming = bookings.filter(b => new Date(b.date + "T23:59:00") >= new Date());
  const past     = bookings.filter(b => new Date(b.date + "T23:59:00") <  new Date());

  return (
    <div className="mb-page">
      <div className="mb-container">

        {/* Header */}
        <div className="mb-header">
          <p className="mb-header-eyebrow">Iyengar Yoga · St. Petersburg</p>
          <h1 className="mb-header-title">My Bookings</h1>
        </div>

        {/* Success banner */}
        {success && (
          <SuccessBanner type={success} onDismiss={() => setSuccess(null)} />
        )}

        {/* Email lookup */}
        {!submitted ? (
          <div className="mb-lookup-card">
            <p className="mb-lookup-text">
              Enter the email address you used when booking to view and manage your upcoming classes.
            </p>
            <div className="mb-lookup-row">
              <input
                type="email"
                className="mb-input mb-lookup-input"
                placeholder="your@email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(null); }}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                autoFocus
              />
              <button className="mb-btn-primary" onClick={handleSubmit}>
                Find bookings
              </button>
            </div>
            {error && <p className="mb-error">{error}</p>}
          </div>
        ) : (
          <>
            <div className="mb-email-bar">
              <span>Showing bookings for <strong>{email}</strong></span>
              <button
                className="mb-change-email"
                onClick={() => { setSubmitted(false); setBookings([]); setSuccess(null); }}
              >
                Change
              </button>
            </div>

            {loading && (
              <div className="mb-loading">
                <div className="mb-spinner" />
                <span>Loading your bookings…</span>
              </div>
            )}

            {error && <p className="mb-error mb-error-standalone">{error}</p>}

            {!loading && !error && bookings.length === 0 && (
              <div className="mb-empty">
                <p>No bookings found for this email address.</p>
                <p className="mb-empty-sub">
                  If you think this is an error, please{" "}
                  <a href="/contact" className="mb-link">get in touch</a>.
                </p>
              </div>
            )}

            {!loading && activePass && (
              <div className="mb-pass-balance">
                <span className="mb-pass-label">{PASS_LABELS[activePass.type]}</span>
                <span className="mb-pass-remaining">
                  {activePass.classesTotal - activePass.classesUsed} of {activePass.classesTotal} classes remaining
                </span>
                {activePass.expiresAt && (
                  <span className="mb-pass-expiry">
                    Expires {new Date(activePass.expiresAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </div>
            )}

            {!loading && upcoming.length > 0 && (
              <section className="mb-section">
                <h2 className="mb-section-title">Upcoming</h2>
                {upcoming.map(b => (
                  <BookingCard
                    key={b._id}
                    booking={b}
                    isHighlighted={b._id === preloadId}
                    onReschedule={booking => setModal({ type: "reschedule", booking })}
                    onCancel={booking => setModal({ type: "cancel", booking })}
                  />
                ))}
              </section>
            )}

            {!loading && past.length > 0 && (
              <section className="mb-section mb-section-past">
                <h2 className="mb-section-title">Past</h2>
                {past.map(b => (
                  <BookingCard
                    key={b._id}
                    booking={b}
                    isHighlighted={false}
                    onReschedule={() => {}}
                    onCancel={() => {}}
                  />
                ))}
              </section>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {modal?.type === "reschedule" && (
        <RescheduleModal
          booking={modal.booking}
          email={email.trim().toLowerCase()}
          onDone={handleDone}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "cancel" && (
        <CancelModal
          booking={modal.booking}
          email={email.trim().toLowerCase()}
          onDone={handleDone}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )};